import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver-es';
import { catchError, filter, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  ApiActionOriginResponse,
  ApiActionStatusResponse,
  ApiActionTagResponse,
  ApiActionTypeResponse,
  ApiCreateLinkingImageRequest,
  ApiCreateStoryRequest,
  ApiDashboardSettingsResponse,
  ApiFeedbackSettingsResponse,
  ApiLinkingImageResponse,
  ApiListActionOriginResponse,
  ApiListActionStatusesResponse,
  ApiListActionTagsResponse,
  ApiListActionTypesResponse,
  ApiListBoardsResponse,
  ApiListStakeholdersResponse,
  ApiStakeholderResponse,
  ApiStoryResponse,
  ModelsActionOriginType,
  ModelsActionTagColour,
} from '../../api/core/models';
import { BoardResponse } from '../../api/core/models/api/board-response';
import { SpinUpBoardRequest } from '../../api/core/models/api/spin-up-board-request';
import { V3RequiredFieldsResponse } from '../../api/core/models/api/v-3-required-fields-response';
import {
  BoardSettingsService,
  BoardsService,
  DashboardSettingsService,
  FeedbackService,
  ReductionGoalsService,
  StakeholdersService,
  StoriesService,
  TransitionStoryGroupsService,
} from '../../api/core/services';
import { TransitionElementHierarchiesService } from '../../api/data-definitions/services';
import { HIERARCHY_NAME_TE_SUPPLY } from '../../app.constants';
import { ClientBoard } from '../../model/client-model/client-board';
import { Language } from '../../model/enums/language';
import { ACTION_STATUS, ClientBoardV2 } from '../backend-service/model';
import { ModelV2Service } from '../backend-service/model-v2.service';
import { ACTION_STATUS_ALLOWED } from '../backend-service/model/action-status';
import { ClientBoardSettings } from '../backend-service/model/client-board-settings';
import { STAKEHOLDER_TYPE_ALLOWED } from '../backend-service/model/stakeholder-type';
import { BoardFacadeService } from '../store/board/board-facade.service';

@Injectable({
  providedIn: 'root',
})
export class BackendBoardsV2Service {
  constructor(
    private _apiBoardSettingsService: BoardSettingsService,
    private _apiBoardsService: BoardsService,
    private _apiStoriesService: StoriesService,
    private _dashboardSettingsService: DashboardSettingsService,
    private _feedbackService: FeedbackService,
    private _modelServiceV2: ModelV2Service,
    private _reductionGoalService: ReductionGoalsService,
    private _stakeholderService: StakeholdersService,
    private _boardFacade: BoardFacadeService,
    private _transitionElementHierarchiesService: TransitionElementHierarchiesService,
    private _transitionStoryHierarchiesService: TransitionStoryGroupsService,
    private _router: Router
  ) {}

  copyBoard(boardId: string, tenantId: string): Observable<string> {
    return this._apiBoardsService.copyBoard({ boardId, body: { tenantId } });
  }

  createActionStatus({
    boardId,
    language,
    name,
    status,
    sortOrder,
    statusId,
  }: {
    boardId: string;
    language: Language;
    name: string;
    status: ACTION_STATUS_ALLOWED;
    sortOrder: number;
    statusId: string;
  }): Observable<ApiActionStatusResponse> {
    return this._apiBoardSettingsService.createActionStatus({
      boardId,
      body: { id: statusId, displayValue: { [language]: name }, status, sortOrder },
    });
  }

  createActionTag({
    boardId,
    language,
    name,
    sortOrder,
    tagId,
  }: {
    boardId: string;
    language: Language;
    name: string;
    sortOrder: number;
    tagId: string;
  }): Observable<ApiActionTagResponse> {
    return this._apiBoardSettingsService.createActionTag({
      boardId,
      body: { id: tagId, displayValue: { [language]: name }, sortOrder },
    });
  }

  createActionType({
    boardId,
    language,
    name,
    sortOrder,
    typeId,
  }: {
    boardId: string;
    language: Language;
    name: string;
    sortOrder: number;
    typeId: string;
  }): Observable<ApiActionTypeResponse> {
    return this._apiBoardSettingsService.createActionType({
      boardId,
      body: { displayValue: { [language]: name }, sortOrder, id: typeId },
    });
  }

  createActionOrigin({
    boardId,
    originId,
    language,
    name,
    originType,
    description,
    sortOrder,
  }: {
    boardId: string;
    language: Language;
    name: string;
    originId: string;
    originType: ModelsActionOriginType;
    description: string;
    sortOrder: number;
  }): Observable<ApiActionOriginResponse> {
    return this._apiBoardSettingsService.createActionOrigin({
      boardId,
      body: {
        id: originId,
        title: { [language]: name },
        type: originType,
        description: description ? { [language]: description } : {},
        sortOrder,
      },
    });
  }

  createEmissionStory(board: ClientBoardV2) {
    const {
      boardSettings: {
        internationalisation: { defaultLanguage },
      },
    } = board;
    const body = this._modelServiceV2.clientBoardToUpdateEmissionStoryRequest(
      board,
      defaultLanguage
    ) as ApiCreateStoryRequest;
    return this._apiStoriesService.createBoardEmissionStory({ boardId: board.id, body });
  }

  createFeedbackSettings(board: ClientBoardV2) {
    const {
      boardSettings: {
        internationalisation: { defaultLanguage },
      },
    } = board;
    const body = this._modelServiceV2.toCreateFeedbackSettings(board);
    return this._dashboardSettingsService.createFeedbackSettings({
      boardId: board.id,
      'Accept-Language': defaultLanguage,
      body,
    });
  }

  createFooterLogo(boardId: string, logo: Omit<ApiCreateLinkingImageRequest, 'altText'>) {
    return this._dashboardSettingsService.createFooterLogo({ boardId, body: { ...logo, altText: {} } });
  }

  createStakeholder({
    boardId,
    language,
    name,
    stakeholder,
    sortOrder,
    stakeholderId,
  }: {
    boardId: string;
    language: Language;
    name: string;
    stakeholder: STAKEHOLDER_TYPE_ALLOWED;
    sortOrder: number;
    stakeholderId: string;
  }): Observable<ApiStakeholderResponse> {
    return this._stakeholderService.createStakeholder({
      boardId,
      body: { id: stakeholderId, name: { [language]: name }, stakeholder, sortOrder },
    });
  }

  deleteActionStatus(boardId: string, statusId: string): Observable<string> {
    return this._apiBoardSettingsService.deleteActionStatus({ boardId: boardId, id: statusId });
  }

  deleteActionTag(boardId: string, tagId: string): Observable<string> {
    return this._apiBoardSettingsService.deleteActionTag({ boardId: boardId, id: tagId });
  }

  deleteActionType(boardId: string, typeId: string): Observable<string> {
    return this._apiBoardSettingsService.deleteActionType({ boardId: boardId, id: typeId });
  }

  deleteActionOrigin(boardId: string, originId: string): Observable<string> {
    return this._apiBoardSettingsService.deleteActionOrigin({ boardId: boardId, id: originId });
  }

  deleteBoard(boardId: string): Observable<string> {
    return this._apiBoardsService.deleteBoard({ id: boardId });
  }

  deleteFooterLogos(boardId: string, footerLogoId: string) {
    return this._dashboardSettingsService.deleteFooterLogos({ boardId, id: footerLogoId });
  }

  deleteEmissionStory(boardId: string): Observable<string> {
    return this._apiStoriesService.deleteBoardEmissionStory({ boardId: boardId });
  }

  deleteStakeholder(boardId: string, stakeholderId: string): Observable<string> {
    return this._stakeholderService.deleteStakeholder({ boardId, id: stakeholderId });
  }

  exportBoard(boardId: string): Observable<void> {
    return this._apiBoardsService.exportBoard({ boardId }).pipe(
      map((data) => {
        const boardTitle = (data as any)['coreData']['Title'];
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        saveAs(blob, `${boardTitle}.json`);
      })
    );
  }

  getActionOrigins(boardId: string, language: Language): Observable<ApiListActionOriginResponse | undefined> {
    return this._apiBoardSettingsService
      .listActionOrigins({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getActionTags(boardId: string, language: Language): Observable<ApiListActionTagsResponse | undefined> {
    return this._apiBoardSettingsService
      .listActionTags({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getActionTypes(boardId: string, language: Language): Observable<ApiListActionTypesResponse | undefined> {
    return this._apiBoardSettingsService
      .listActionTypes({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getActionStatuses(boardId: string, language: Language): Observable<ApiListActionStatusesResponse | undefined> {
    return this._apiBoardSettingsService
      .listActionStatuses({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getApiBoard(boardId: string): Observable<BoardResponse> {
    return this._apiBoardsService.getBoard({ id: boardId });
  }

  getApiBoardSettings(boardId: string): Observable<ClientBoardSettings> {
    return this._apiBoardSettingsService
      .getBoardSettings({ boardId: boardId })
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this._router.navigateByUrl('/');
          }
          return of();
        })
      )
      .pipe(
        filter((v) => v !== undefined),
        map((apiBoardSettings) => this._modelServiceV2.toClientBoardSettings(apiBoardSettings))
      );
  }

  getClientBoard(boardId: string): Observable<ClientBoardV2> {
    return forkJoin([
      this.getApiBoardSettings(boardId),
      this._reductionGoalService.listReductionGoals({ boardId }),
    ]).pipe(
      switchMap(([boardSettings, apiReductionGoals]) => {
        const language = boardSettings.primaryLanguage;
        if (!language) {
          throw new Error(`Board has no primary language. Board id: ${boardId}`);
        }
        return forkJoin({
          board: this.getApiBoard(boardId),
          dashboardSettings: this.getDashboardSettings(boardId, language),
          emissionStory: this.getEmissionStory(boardId, language),
          mainStory: this.getMainStory(boardId, language),
          apiStakeholders: this.getStakeholders(boardId, language),
          apiActionOrigins: this.getActionOrigins(boardId, language),
          apiActionTags: this.getActionTags(boardId, language),
          apiActionTypes: this.getActionTypes(boardId, language),
          actionStatuses: this.getActionStatuses(boardId, language),
          apiFeedbackSettings: this.getFeedbackSettings(boardId, language),
          footerLogos: this.getFooterLogos(boardId, language),
          apiV3RequiredFields: this.getV3RequiredFields(boardId),
          consumptionHierarchy: this._transitionStoryHierarchiesService.listTransitionStoryGroups({
            boardId,
            'Accept-Language': language,
          }),
          supplyHierarchy: this._transitionElementHierarchiesService.getTransitionElementHierarchyByName({
            name: HIERARCHY_NAME_TE_SUPPLY,
            'Accept-Language': language,
          }),
        }).pipe(
          map((result) => {
            return this._modelServiceV2.boardResponseToClientBoard({ ...result, boardSettings, apiReductionGoals });
          })
        );
      })
    );
  }

  getBoards(tenantId?: string): Observable<ApiListBoardsResponse> {
    return this._apiBoardsService.listBoards({ organisationId: tenantId });
  }

  getDashboardSettings(boardId: string, language: Language): Observable<ApiDashboardSettingsResponse> {
    return this._dashboardSettingsService.getDashboardSettings({ boardId: boardId, 'Accept-Language': language });
  }

  getEmissionStory(boardId: string, language: Language): Observable<ApiStoryResponse | undefined> {
    return this._apiStoriesService
      .getBoardEmissionStory({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getFeedbackSettings(boardId: string, language: Language): Observable<ApiFeedbackSettingsResponse | undefined> {
    return this._dashboardSettingsService
      .getFeedbackSettings({ boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getFooterLogos(boardId: string, language: Language): Observable<ApiLinkingImageResponse[]> {
    return this._dashboardSettingsService
      .listFooterLogos({ boardId, 'Accept-Language': language })
      .pipe(map((res) => res?.linkingImages ?? []));
  }

  getMainStory(boardId: string, language: Language): Observable<ApiStoryResponse> {
    return this._apiStoriesService.getBoardMainStory({ boardId: boardId, 'Accept-Language': language });
  }

  getStakeholders(boardId: string, language: Language): Observable<ApiListStakeholdersResponse | undefined> {
    return this._stakeholderService
      .listStakeholders({ boardId: boardId, 'Accept-Language': language })
      .pipe(catchError(() => of(undefined)));
  }

  getV3RequiredFields(boardId: string): Observable<V3RequiredFieldsResponse | undefined> {
    return this._dashboardSettingsService.getV3RequiredFields({ boardId }).pipe(catchError(() => of(undefined)));
  }

  postFeedback(
    boardId: string,
    body: { email: string; message: string; name: string; subject: string }
  ): Observable<boolean> {
    return this._feedbackService.postFeedback({ boardId, body }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  spinUpBoard(spinUpRequest: SpinUpBoardRequest): Observable<string> {
    return this._apiBoardsService.spinUpBoard({ body: spinUpRequest });
  }

  updateActionStatus({
    name,
    status,
    language,
    boardId,
    statusId,
    sortOrder,
  }: {
    name: string;
    status: ACTION_STATUS;
    language: Language;
    boardId: string;
    statusId: string;
    sortOrder?: number;
  }): Observable<string> {
    const body = this._modelServiceV2.toUpdateActionStatusRequest(name, status, language, sortOrder);
    return this._apiBoardSettingsService.updateActionStatus({ boardId, id: statusId, body });
  }

  updateActionTag({
    boardId,
    actionTagId,
    language,
    name,
    sortOrder,
    colour,
  }: {
    boardId: string;
    actionTagId: string;
    language: Language;
    name: string;
    sortOrder?: number;
    colour: ModelsActionTagColour;
  }) {
    const body = this._modelServiceV2.toUpdateActionTagRequest(name, language, colour, sortOrder);
    return this._apiBoardSettingsService.updateActionTag({
      id: actionTagId,
      boardId,
      body,
    });
  }

  updateActionType({
    name,
    language,
    boardId,
    typeId,
    sortOrder,
  }: {
    name: string;
    language: Language;
    boardId: string;
    typeId: string;
    sortOrder?: number;
  }): Observable<string> {
    const body = this._modelServiceV2.toUpdateActionTypeRequest(name, language, sortOrder);
    return this._apiBoardSettingsService.updateActionType({ boardId, id: typeId, body });
  }

  updateActionOrigin({
    name,
    language,
    boardId,
    originId,
    originType,
    description,
    sortOrder,
  }: {
    name: string;
    language: Language;
    boardId: string;
    originId: string;
    originType: ModelsActionOriginType;
    description: string;
    sortOrder?: number;
  }): Observable<ApiActionOriginResponse> {
    return this._apiBoardSettingsService.updateActionOrigin({
      boardId,
      id: originId,
      body: {
        title: { [language]: name },
        type: originType,
        description: description ? { [language]: description } : {},
        ...(sortOrder !== undefined ? { sortOrder } : {}),
      },
    });
  }

  updateApiBoardSettings(board: ClientBoardV2): Observable<string> {
    const body = this._modelServiceV2.clientBoardToUpdateBoardSettingsRequest(board.boardSettings);
    return this._apiBoardSettingsService.updateBoardSettings({ boardId: board.id, body: body });
  }

  updateBoard(board: ClientBoardV2 | ClientBoard): Observable<string> {
    const body = this._modelServiceV2.clientBoardToUpdateBoardRequest(board);
    return this._apiBoardsService.updateBoard({ id: board.id, body: body });
  }

  updateDashboardSettings(board: ClientBoardV2): Observable<string> {
    const body = this._modelServiceV2.toUpdateDashboardSettingsRequest(board);
    return this._dashboardSettingsService.updateDashboardSettings({ boardId: board.id, body });
  }

  updateFooterLogo(
    boardId: string,
    { id, destinationUrl, sourceUrl }: { id: string; destinationUrl?: string; sourceUrl?: string }
  ) {
    return this._dashboardSettingsService.updateFooterLogos({
      boardId,
      id: id,
      body: { altText: {}, ...(destinationUrl ? { destinationUrl } : {}), ...(sourceUrl ? { sourceUrl } : {}) },
    });
  }

  updateEmissionStory(board: ClientBoardV2): Observable<string> {
    const {
      boardSettings: {
        internationalisation: { defaultLanguage },
      },
    } = board;
    const body = this._modelServiceV2.clientBoardToUpdateEmissionStoryRequest(board, defaultLanguage);
    return this._apiStoriesService.updateBoardEmissionStory({ boardId: board.id, body });
  }

  updateOrDeleteFeedbackSettings(board: ClientBoardV2): Observable<string> {
    const body = this._modelServiceV2.toUpdateFeedbackSettingsRequest(board.boardSettings);
    if (Object.keys(body).length !== 4) {
      return this._dashboardSettingsService.deleteFeedbackSettings({ boardId: board.id });
    }
    return this._dashboardSettingsService.updateFeedbackSettings({ boardId: board.id, body });
  }

  updateMainStory(board: ClientBoardV2): Observable<string> {
    const {
      boardSettings: {
        internationalisation: { defaultLanguage },
      },
    } = board;
    const body = this._modelServiceV2.clientBoardToUpdateMainStoryRequest(board, defaultLanguage);
    return this._apiStoriesService.updateBoardMainStory({ boardId: board.id, body });
  }

  updateStakeholders({
    boardId,
    name,
    stakeholder,
    language,
    stakeholderId,
    sortOrder,
  }: {
    stakeholderId: string;
    boardId: string;
    name: string;
    stakeholder: STAKEHOLDER_TYPE_ALLOWED;
    language: Language;
    sortOrder?: number;
  }): Observable<string> {
    const body = this._modelServiceV2.toUpdateStakeholderRequest(name, stakeholder, language, sortOrder);
    return this._stakeholderService.updateStakeholder({ boardId, id: stakeholderId, body });
  }

  updateV3RequiredFields(board: ClientBoardV2) {
    const body = this._modelServiceV2.toUpdateV3RequiredFieldsRequest(board) as any;
    return this._dashboardSettingsService.updateV3RequiredFields({ boardId: board.id, body }).pipe(
      catchError(() => {
        return this._dashboardSettingsService.createV3RequiredFields({ boardId: board.id, body }).pipe(
          catchError(() => {
            return of(undefined);
          })
        );
      })
    );
  }
}
